// fastTrackUpsell.js
// Priority Physician Review — the post-purchase charge.
//
// The add-on used to be a pre-ticked box inside checkout. It is now offered AFTER
// the order is captured, from a yes/no modal, and is therefore a second,
// independent charge: POST /api/upsell-checkout. Nothing in here can affect the
// order that already succeeded — a decline, a timeout or a dead endpoint costs the
// patient nothing and leaves their prescription untouched.
//
// The price comes from the resolved catalog record (fastTrackAddon.js), never from
// a literal here, so flipping the product inactive removes the offer with no deploy.
//
// Two ways in, because the two payment paths end in different places:
//
//   • Card / Apple Pay / Google Pay / Link — omit `card`. `useExistingCard` bills
//     the card the order was just placed with; nothing is re-collected. The wallets
//     post a real Stripe PaymentMethod to /api/checkout-stripe exactly as a typed
//     card does, so there is a card on file for all four.
//   • Klarna — pass `card`. Klarna authorizes one fixed amount up front and leaves
//     nothing on file, so there is no card for `useExistingCard` to find. The modal
//     tokenizes one and hands the token over here.
//
// Payload shape is the one already proven against this backend by the
// WeightLossAfterCheckout upsell, including its empty `billingAddress` — the
// backend reads the customer's stored Stripe address, so the modal collects no
// address of its own.

import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { resolveFastTrackAddon } from '@/utils/fastTrackAddon';
import { getProducts, loadCachedProducts, initializeProducts } from '@/utils/productData';
import { needsBankCheck, recordConfirmedUpsell, runBankChecks, shopperMessage } from '@/utils/bankCheck';

const NOT_ADDED = 'We could not add priority review. Your order is still confirmed.';

/**
 * funnel_step for the add-on's own `purchase`.
 *
 * The order's purchase is step 15 (WeightLossAfterCheckout). This charge is a
 * separate transaction and needs a step of its own — reusing 15 would hand the ad
 * platforms two purchases on one step number, which is how one of them gets
 * dropped as a duplicate. 16 is the next free number; nothing else claims it.
 */
export const FAST_TRACK_UPSELL_FUNNEL_STEP = 16;

/** Same budget the main checkout call uses. */
const UPSELL_TIMEOUT_MS = 30000;

/** Shared catalog request so four surfaces can never stack up fetches. */
let catalogRequest = null;

/**
 * Resolve the add-on, waiting for the catalog if it has not landed yet.
 *
 * App.vue loads /api/get-all-products at boot, so the in-memory cache is normally
 * already warm. The Klarna return page is a fresh page load, though, and that boot
 * fetch may still be in flight when this is called — hence the fallback chain:
 * memory, then localStorage, then the network.
 *
 * Returns { product, plan } or null. null is a real, expected outcome (the product
 * does not exist in every environment) and means "do not offer it": no modal, no
 * charge. There is deliberately no fallback price and no fallback product id.
 */
export const loadFastTrackAddon = async (baseUrl) => {
    try {
        let products = getProducts();

        if (!Array.isArray(products) || products.length === 0) {
            loadCachedProducts();
            products = getProducts();
        }

        if (!Array.isArray(products) || products.length === 0) {
            products = await fetchCatalog(baseUrl);
        }

        return resolveFastTrackAddon(products);
    } catch (error) {
        console.error('[fast-track] add-on could not be resolved:', error);
        return null;
    }
};

const fetchCatalog = async (baseUrl) => {
    const origin = String(baseUrl || process.env.VUE_APP_API_ENDPOINT || '').replace(/\/+$/, '');
    if (!origin) return null;

    if (!catalogRequest) {
        catalogRequest = axios
            .get(`${origin}/api/get-all-products`)
            .then((response) => {
                const products = response?.data?.products;
                if (!Array.isArray(products) || products.length === 0) return null;
                // Seed the in-memory cache the way App.vue does, but leave its
                // localStorage entry alone — the timestamp bookkeeping is App.vue's.
                initializeProducts(products);
                return products;
            })
            .catch((error) => {
                console.error('[fast-track] product catalog fetch failed:', error);
                return null;
            })
            .finally(() => {
                catalogRequest = null;
            });
    }

    return catalogRequest;
};

/**
 * Billing duration of THIS purchase, read off the catalog plan.
 *
 * Not the parent order's term. The one working call on this endpoint
 * (WeightLossAfterCheckout) sends payment_terms equal to its own cart line's
 * `terms`, so that invariant is held here rather than inventing a second meaning
 * for the field. The live Priority Physician Review plan says 0 (one-time); if the
 * catalog ever changes it, the charge follows the catalog instead of a number
 * frozen into this file.
 */
const resolveTerms = (addon) => Number(addon.plan.terms || 0);

/**
 * The one cart line this charge carries.
 *
 * `id` and `product_id` are the same ObjectId: the main checkout's cart lines key
 * off `id` and the proven upsell payload keys off `product_id`, so both are sent
 * rather than betting on which one the endpoint reads. They cannot disagree.
 */
const buildCartLine = (addon, fee) => ({
    id: addon.product.id,
    product_id: addon.product.id,
    plan_id: addon.plan.id || addon.plan.plan_id || '',
    name: addon.product.name,
    price: fee,
    retail_price: fee,
    terms: resolveTerms(addon),
    quantity: 1,
    // Read off the catalog record rather than asserted, and consistent with the
    // totals below: this order is one line, so that line is the whole of
    // mainProductTotal and addonTotal is 0.
    is_addon: addon.product.is_addon === true,
});

const normalizeAddress = (address) => ({
    addressLine1: (address && address.addressLine1) || '',
    addressLine2: (address && address.addressLine2) || '',
    city: (address && address.city) || '',
    state: (address && address.state) || '',
    zip: (address && address.zip) || '',
});

/** Fresh idempotency key per attempt, matching every other charge in this app. */
const newCorrelationId = () => {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    } catch (e) {
        console.log('correlation id generation failed', e);
        return '';
    }
};

/**
 * Charge the add-on.
 *
 * Never throws: the caller is rendering a modal on top of a completed order and has
 * to show a message, not a stack trace. Every failure comes back as
 * { ok: false, message }.
 */
export const chargeFastTrackUpsell = async (args) => {
    const origin = String(args.baseUrl || process.env.VUE_APP_API_ENDPOINT || '').replace(/\/+$/, '');
    if (!origin) return { ok: false, message: 'Payment is unavailable right now.' };
    if (!args.userId) return { ok: false, message: 'We could not find your account. Please try again.' };

    const fee = Number(Number(args.addon.plan.price).toFixed(2));
    if (!Number.isFinite(fee) || fee <= 0) {
        return { ok: false, message: 'Priority review is unavailable right now.' };
    }

    const body = {
        id: args.userId,
        user_id: args.userId,
        payment_terms: resolveTerms(args.addon),

        // `useExistingCard` tells the backend to bill the card the order was just
        // placed with, and is the only mode a card or wallet order ever uses. A
        // Klarna order has no such card, so it sends a token instead and this flips
        // to false — leaving it true there would ask the backend to charge nothing.
        useExistingCard: !args.card,
        is_upsell: true,
        paymentMethod: 'card',
        cardToken: (args.card && args.card.token) || '',
        cardBin: '',
        cardBrand: '',
        cardLast4: '',
        cardDetails: [],

        amount: Math.round(fee * 100),
        discount: 0,
        couponCode: '',
        couponId: null,
        // Empty on purpose, exactly as the existing upsell sends it. On the main
        // checkout `planId` is what the server charges off; here the cart line is,
        // and naming a plan as well is how you get billed twice for one add-on.
        planId: '',
        medication: '',
        drug_mode: '',
        offer: '',

        cartItems: [buildCartLine(args.addon, fee)],
        cartTotal: fee,
        cartTotalNoDiscount: fee,
        mainProductTotal: fee,
        addonTotal: 0,
        totalOrderAmount: fee,

        firstName: args.firstName || '',
        lastName: args.lastName || '',
        email: args.email || '',
        // Placeholders, as the existing upsell sends them — the backend bills against
        // the customer's stored Stripe address, so nothing is re-collected here.
        billingAddress: {},
        shippingAddress: normalizeAddress(args.shippingAddress),

        tracking_session_id: args.trackingSessionId || 'unknown',
        ip_address: args.ipAddress || '',
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || 'unknown',
    };

    const post = (payload) => axios.post(`${origin}/api/upsell-checkout`, payload, {
        headers: { 'X-Correlation-Id': newCorrelationId() },
        timeout: UPSELL_TIMEOUT_MS,
    });

    try {
        const response = await post(body);
        const data = response && response.data;

        if (data && data.status === 'success') {
            const transactionId = data.transaction_id || data.transactionId || data.pendingAuthId || '';
            return {
                ok: true,
                fee,
                transactionId,
                orderId: data.order_id || data.orderId || transactionId,
            };
        }

        // A card whose bank wants to confirm: the backend holds the charge and hands
        // back its client secret. The charge goes through only once the bank's popup
        // passes, and the re-post records it; otherwise nothing was charged.
        if (needsBankCheck(data)) {
            const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
            const bankCheck = await runBankChecks(stripe, [data.client_secret]);
            if (!bankCheck.ok) return { ok: false, message: NOT_ADDED };
            await recordConfirmedUpsell(post, body, data.payment_intent_id);
            const transactionId = data.payment_intent_id || '';
            return { ok: true, fee, transactionId, orderId: transactionId };
        }

        return { ok: false, message: shopperMessage(data && data.message, NOT_ADDED) };
    } catch (error) {
        console.error('[fast-track] priority review upsell failed:', error);
        // A backend without the bank check refuses that card with Stripe's status
        // text: the patient gets the plain line instead.
        return { ok: false, message: shopperMessage(error?.response?.data?.message, NOT_ADDED) };
    }
};

/**
 * Fire the add-on's own `purchase`.
 *
 * It gets its own event rather than being folded into the order's: that one already
 * fired for the medication alone, and adding this revenue to it would mean a second
 * event wearing the same ids. For the same reason transaction_id and order_id are
 * carried ONLY when the server returned them — reusing the order's would double-count
 * the transaction, and inventing an id would be worse.
 */
export const fireFastTrackUpsellPurchase = (addon, result) => {
    try {
        if (typeof window === 'undefined' || !window.tracking || !window.tracking.event) return;

        window.tracking.event('purchase', {
            funnel_step: FAST_TRACK_UPSELL_FUNNEL_STEP,
            event_value: result.fee,
            currency: 'USD',
            event_id: newCorrelationId(),
            product_id: addon.product.id,
            product_name: addon.product.name,
            quantity: 1,
            label: 'fast_track_upsell',
            ...(addon.plan.id ? { plan_id: addon.plan.id } : {}),
            ...(result.transactionId ? { transaction_id: result.transactionId } : {}),
            ...(result.orderId ? { order_id: result.orderId } : {}),
        });
    } catch (error) {
        console.error('[fast-track] upsell purchase tracking failed:', error);
    }
};
